import { TrashIcon } from '@heroicons/react/24/outline'
import Tippy from '@tippyjs/react'
import Image from 'next/image'
import { FC, useEffect, useRef, useState } from 'react'
import { type Swiper as SwiperType } from 'swiper'
import { Mousewheel } from 'swiper/modules'
import { Swiper as SwiperComponent, SwiperSlide } from 'swiper/react'
import { Instance } from 'tippy.js'

import { PaymentCard, PaySystem } from '@/api/models/finance'
import { useCardDelete } from '@/api/models/finance/hooks/useCardDelete'
import { ScreenSize } from '@/app/constants'
import { useCurrentBreakpoint } from '@/app/hooks/useCurrentBreakpoint'
import { cn } from '@/app/utils'
import { payMethodsOptions } from '@/components/pages/bill/ui/BillsPayDrawer/BillsPayDrawer.constants'

import { ConfirmSubmitModal } from '../../modal'
import { PaySystemImage } from './PaySystemImage'

type Props = {
  options: PaymentCard[]
  selectedCard?: PaymentCard
  onCardSelect: (selectedCard?: PaymentCard) => void
  cardsQuantity: number
}

const CardsGroup: FC<Props> = ({
  selectedCard,
  options,
  onCardSelect,
  cardsQuantity
}) => {
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false)
  const [cardIdToDelete, setCardIdToDelete] = useState<string>('')
  const [needResetToStart, setNeedResetToStart] = useState(false)
  const { mutateAsync: deleteCard } = useCardDelete()
  const swiperRef = useRef<SwiperType | null>(null)
  const tippyRef = useRef<Instance | null>(null)
  const { breakpoint } = useCurrentBreakpoint()
  const isMobileScreen = [ScreenSize.Xxs].includes(breakpoint)
  const isTwoCards = options.length === 2
  const isScrollable = options.length > 2

  const onSlideClick = (index: number) => {
    swiperRef?.current?.slideTo(index)
  }

  useEffect(() => {
    if (!needResetToStart) {
      return
    }

    requestAnimationFrame(() => {
      swiperRef?.current?.slideTo(0, 0)
      swiperRef?.current?.update()
      swiperRef?.current?.setTranslate(0)
      swiperRef?.current?.updateProgress()
      swiperRef?.current?.updateSlidesClasses()
    })
  }, [options.length, needResetToStart])

  const onCardDelete = async () => {
    if (!cardIdToDelete) {
      return
    }

    onCardSelect(options[0].id === cardIdToDelete ? options[1] : options[0])

    await deleteCard(cardIdToDelete)
    setNeedResetToStart(true)
    setIsConfirmModalOpen(false)
  }

  const onDeleteButtonClick = (cardId: string) => {
    setCardIdToDelete(cardId)
    setIsConfirmModalOpen(true)

    // помогаем типпи закрываться при открытии модалки т.к. он не теряет ховер
    if (tippyRef.current) {
      tippyRef.current.hide()
    }
  }

  return (
    <>
      <SwiperComponent
        key={options.length}
        modules={[Mousewheel]}
        slidesPerView={isTwoCards ? 2 : 'auto'}
        spaceBetween={8}
        centeredSlidesBounds
        mousewheel
        watchOverflow
        threshold={10}
        className={cn(
          'flex w-full gap-12',
          // на мобилке карточки должны уезжать под края экрана,
          // поэтому гасим боковые отступы дровера
          isScrollable && 'sm:!-mx-16 sm:w-auto sm:!pl-16'
        )}
        onInit={(swiper: SwiperType) => {
          swiperRef.current = swiper
        }}
      >
        {options.map((option, index) => (
          <SwiperSlide
            key={option.id}
            className={cn(
              isScrollable &&
                index === options.length - 1 &&
                'pr-40 lg:pr-24 sm:pr-16',
              isScrollable && index === 0 && 'pl-40 lg:pl-24 sm:pl-0',
              options.length === 1 &&
                'max-sm:px-0 w-full px-40 lg:px-24 sm:px-0',

              options.length === 2 &&
                cn(
                  'w-[calc(50%-4px)]',
                  index === 0 && 'max-sm:pl-0 pl-40 lg:pl-24 sm:pl-0',
                  index === 1 && 'max-sm:pr-0 pr-40 lg:pr-24 sm:pr-0'
                ),

              isScrollable && '!w-auto'
            )}
            onClick={() => onSlideClick(index)}
          >
            <button
              type="button"
              key={option.id}
              className={cn(
                'flex h-[128px] w-full cursor-pointer flex-col justify-between rounded-12 border bg-grey-10 p-12 text-left',
                {
                  'border-2 border-bronze-40': option.id === selectedCard?.id,
                  'border-grey-40': option.id !== selectedCard?.id,
                  'w-full xxs:h-[100px]': options.length === 1 || isTwoCards,
                  'w-[350px] lg:w-[312px] xxs:h-[100px] xxs:w-[167px]':
                    isScrollable
                }
              )}
              onClick={() => onCardSelect(option)}
            >
              <div className="flex justify-between">
                <PaySystemImage paySystem={option.paySystem} />
                <div
                  className={cn(
                    'h-20 w-20 rounded-full border border-bronze-40',
                    option.id === selectedCard?.id && 'border-[6px]'
                  )}
                />
              </div>
              {option.number && (
                <div className="flex justify-between">
                  <div className="flex items-center gap-4">
                    <div className="h-4 w-4 rounded-16 bg-grey-100" />
                    <div className="mr-4 h-4 w-4 rounded-16 bg-grey-100" />
                    <div className="font-semibold text-16">{option.number}</div>
                  </div>
                  <Tippy
                    content={
                      <div className="rounded-12 bg-grey-100">
                        <p className="p-12 font-pro text-white text-16">
                          Удалить карту
                        </p>
                      </div>
                    }
                    trigger="click mouseenter"
                    hideOnClick={true}
                    placement="bottom"
                    className="flex"
                    onMount={instance => (tippyRef.current = instance)}
                  >
                    <button
                      aria-label="Удалить карту"
                      type="button"
                      onClick={() => onDeleteButtonClick(option.id)}
                    >
                      <TrashIcon className="h-28 w-28 text-grey-40 hover:text-grey-100 xs:h-24 xs:w-24" />
                    </button>
                  </Tippy>
                </div>
              )}
              {[PaySystem.Acquiring, PaySystem.Qr].includes(
                option.paySystem
              ) && (
                <div>
                  <div className="font-semibold text-16">
                    {option.id === 'acquiring' && cardsQuantity > 0
                      ? 'Новая карта'
                      : payMethodsOptions.find(
                          methodOption => methodOption.key === option.id
                        )?.name}
                  </div>
                  {!isMobileScreen && (
                    <div className="text-grey-70 text-16">
                      {
                        payMethodsOptions.find(
                          methodOption => methodOption.key === option.id
                        )?.description
                      }
                    </div>
                  )}
                </div>
              )}
            </button>
          </SwiperSlide>
        ))}
      </SwiperComponent>
      <ConfirmSubmitModal
        className="!w-[460px]"
        messageClassName="text-center px-8"
        opened={isConfirmModalOpen}
        header="Удаление карты"
        message="Вы уверены, что хотите удалить эту карту из приложения?"
        closeButtonText="Отмена"
        submitButtonText="Да"
        onClose={() => setIsConfirmModalOpen(false)}
        onSubmit={onCardDelete}
        reverseButtons
        image={
          <Image
            src="/images/balance/Card.png"
            height={140}
            width={140}
            alt="card"
          />
        }
      />
    </>
  )
}

export default CardsGroup
